// Macros: a named sequence of steps run one after another (macroeval.ts works them out, in the macro worker; editui.ts
// and the object page run them, showing each step as it lands, as one undo step).
// So far the steps are the selection rules of the edit toolbar (Box, Normal, Band, …), each combined
// into the selection with its own New / + / − / ∩, and the plain selection buttons (Clear, Invert,
// Erode, Dilate, →Frames, →Scan, select all), another saved macro (run inline), and point groups: select one,
// or add the selection to / take it out of / clear one (made where missing). Those edits are to memory groups, kept by
// the page until it is closed; only a Group save step writes one to the scan (it commits, so a macro that saves is one
// Undo for its saves, memory groups and selections together). Set weight and Blur weights change the points' weights
// (weights.ts, the page's); the data steps (Data group, Data math, Data edit, Plane detector) make and change data
// groups, a number per point (datagroups.ts: set from a source, a point measure, another data group, the nearness of
// other points or the likelihood of lying on a plane (planarity.ts), then combined), worked out in the macro worker and kept
// by the page like memory groups, and Data band selects by their values; the photo steps (PHOTO: Photo features, …) work on the frames' photos, on the page. The pointer tools (lasso, brush, grow) need a place on the screen and
// are not steps. Auto tune (tune.ts) finds the bounds of the selection rules, all of them ∩, that select the
// points it is told to (require) and none of those it is told not to (deny); those lists are the step's own, saved
// with it as point id sets per scan. Any step input can be a variable, <name>: the macro's own (its default kept with
// it, vars) or one a Macro step passes in (args: a value, or the caller's <name>); flatten puts the values in. An input
// (or arg) can also be simple math over variables and numbers, <i+1> or <w*h> (varexpr.ts: + - * / %, brackets). For each
// (t 'blobs', from when it only went over blobs) holds steps of its own (steps), run once for each connected blob of the
// selection, each frame or scan it has points of, or a count, in turn (flatten puts them right after it), with a
// variable of its own (var: the frame's number, else the time round, from 1) that its steps' inputs may name (at most, most, empty or 0: all of them); flatten
// leaves those as a loop token, <#j> (or <#=…>, math over #j with the macro's variables' numbers put in), the run puts the number in (bindLoop). Pure: the catalogue, labels and validation, shared with the editor server,
// which keeps each scene's macros in <scene>/macros.json.
import type { Combine } from './select.ts';
import { type PidSet, decodeSet, encodeSet, maskToPids } from './pidset.ts';
import { GROUP_NAME } from './log.ts';
import { MEASURES } from './dataexpr.ts';
import { type Expr, evalExpr, exprLoops, exprNames, exprText, parseExpr } from './varexpr.ts';
import { ENGINE_FIELDS } from '../mesh/fields.ts';
import { type Graph, compile, graphPorts, graphRuns, nodeSteps, parseGraph } from './nodes.ts';

/**
 * A step's parameter: a number, a fixed choice, a checkbox, or a choice the editor fills in (a volume, a point source).
 * A number with blank may be left empty, kept as 0 (blank says what that means: "all").
 */
type Field0 =
  | { k: 'num'; key: string; label: string; dflt: number; min?: number; max?: number; step?: number; int?: boolean; unit?: string; blank?: string }
  | { k: 'choice'; key: string; label: string; dflt: string; options: [string, string][] }
  | { k: 'bool'; key: string; label: string; dflt: boolean }
  | { k: 'vol' | 'src'; key: string; label: string; dflt: string }
  | { k: 'group' | 'data'; key: string; label: string; dflt: string; blank?: string }
  /** A saved macro's name; graphs: only node graphs with Graph inputs or outputs (a Sub Graph node's). */
  | { k: 'macro'; key: string; label: string; dflt: string; blank?: string; graphs?: true }
  /** A Keep / Recall slot (a node graph's step): a name, or <name>, a slot a Sub Graph node passes in (not a variable of the macro's). */
  | { k: 'slot'; key: string; label: string; dflt: string }
  /** A variable's name (For each's own variable): never a variable itself. */
  | { k: 'var'; key: string; label: string; dflt: string }
  /** Free text (Data eval's expression): up to 200 characters, checked when the step runs. */
  | { k: 'text'; key: string; label: string; dflt: string; placeholder?: string };
/** A field shown (and saved, described) only when the step's field `on[0]` is one of `on[1]`. */
export type Field = Field0 & { on?: [string, string[]] };

/** What a step kind does and takes; combines: it is a rule combined into the selection with a mode. */
/** hidden: a step only node graphs make (nodes.ts compiles them to it), never in the Macros tab's lists. */
export interface Kind { label: string; title: string; combines: boolean; fields: Field[]; hidden?: true }

/** Where a data step's values come from (Data group, Data math): the fields after src, each shown for its sources. */
export const DATA_SRC: [string, string][] = [
  ['const', 'a number'], ['density', 'point density'], ['edges', 'depth edge strength'], ['range', 'capture distance (m)'],
  ['height', 'height above floor (m)'], ['weight', 'point weight'], ['data', 'another data group'], ['near', 'nearness to points (falloff)'],
  ['planar', 'plane likelihood (0 to 1)'],
];
export const CURVES: [string, string][] = [['linear', 'linear'], ['smooth', 'smooth'], ['gauss', 'gaussian'], ['inverse', 'inverse (0 touching, 1 at r)']];
const srcFields = (): Field[] => [
  { k: 'choice', key: 'src', label: 'from', dflt: 'const', options: DATA_SRC },
  { k: 'num', key: 'x', label: '', dflt: 1, step: 0.1, on: ['src', ['const']] },
  { k: 'choice', key: 'dr', label: 'within', dflt: '0.05', options: [['0.02', '2 cm'], ['0.05', '5 cm'], ['0.1', '10 cm'], ['0.2', '20 cm']], on: ['src', ['density']] },
  { k: 'choice', key: 'side', label: '', dflt: 'abs', options: [['abs', 'either side (0 to 1)'], ['signed', 'signed (-1 background to 1 foreground)']], on: ['src', ['edges']] },
  { k: 'data', key: 'of', label: '', dflt: 'data', on: ['src', ['data']] },
  { k: 'src', key: 'from', label: 'to', dflt: 'sel', on: ['src', ['near']] },
  { k: 'num', key: 'r', label: 'within', dflt: 0.1, min: 0.005, max: 5, step: 0.01, unit: 'm', on: ['src', ['near', 'planar']] },
  { k: 'choice', key: 'curve', label: '', dflt: 'linear', options: CURVES, on: ['src', ['near']] },
  ...planeFields(['src', ['planar']]),
];
/** The plane likelihood's leeway (Plane detector; the data steps' planar source): how thick a plane's points may lie, how far their normals stray. */
const planeFields = (on?: [string, string[]]): Field[] => [
  { k: 'num', key: 'tol', label: 'flat to', dflt: 0.03, min: 0.001, max: 0.5, step: 0.005, unit: 'm', ...(on ? { on } : {}) },
  { k: 'num', key: 'deg', label: 'normals to', dflt: 20, min: 1, max: 90, step: 1, unit: '°', ...(on ? { on } : {}) },
];

/** What a For each step goes over (its over), and the most times it runs. */
export const FOR_EACH: [string, string][] = [['blobs', 'blob'], ['frames', 'frame in the selection'], ['scans', 'scan in the selection'], ['count', 'number']];
export const MAX_LOOP = 1000;

/** Every engine's fields, each shown for the engines that have it (a field engines share, e.g. voxels, once). */
function engineFields(): Field[] {
  const out: (Field & { on: [string, string[]] })[] = [];
  for (const [k, e] of Object.entries(ENGINE_FIELDS)) for (const f of e.fields) {
    const had = out.find((x) => x.key === f.key);
    if (had && JSON.stringify({ ...had, on: 0 }) !== JSON.stringify({ ...f, on: 0 })) throw new Error(`mesh engines ${had.on[1]} and ${k} differ on ${f.key}`);
    if (had) had.on[1].push(k);
    else out.push({ ...f, on: ['engine', [k]] });
  }
  return out;
}

export const KINDS = {
  all: { label: 'All shown', title: 'every point shown (Ctrl+A): layers on, not cut away by the view or a hide volume', combines: true, fields: [] },
  box: { label: 'Box', title: 'points in a volume box', combines: true, fields: [{ k: 'vol', key: 'vol', label: 'in', dflt: '*' }] },
  normal: {
    label: 'Normal', title: 'points whose normal points a way', combines: true, fields: [
      { k: 'choice', key: 'axis', label: '', dflt: 'z', options: [['z', 'up/down (z)'], ['x', 'x'], ['y', 'y'], ['h', 'horizontal']] },
      { k: 'num', key: 'deg', label: 'within', dflt: 20, min: 1, max: 90, unit: '°' },
    ],
  },
  band: {
    label: 'Band', title: 'points near a plane: the floor, or one fitted to the selection as it is at this step', combines: true, fields: [
      { k: 'choice', key: 'plane', label: '', dflt: 'floor', options: [['floor', 'height above floor'], ['fit', 'plane fitted to the selection']] },
      { k: 'num', key: 'lo', label: '', dflt: -0.05, step: 0.01 },
      { k: 'num', key: 'hi', label: 'to', dflt: 0.05, step: 0.01, unit: 'm' },
    ],
  },
  distance: {
    label: 'Distance', title: 'points by how far they were from the camera when scanned', combines: true, fields: [
      { k: 'num', key: 'lo', label: 'from', dflt: 0, min: 0, step: 0.1 },
      { k: 'num', key: 'hi', label: 'to', dflt: 3, min: 0, step: 0.1, unit: 'm' },
    ],
  },
  edges: {
    label: 'Edges', title: 'points by the edges of the depth image they came from', combines: true, fields: [
      { k: 'choice', key: 'side', label: '', dflt: 'abs', options: [['abs', 'strength (either side, 0 to 1)'], ['signed', 'signed strength (-1 background to 1 foreground)']] },
      { k: 'num', key: 'lo', label: '', dflt: 0.5, min: -1, max: 1, step: 0.05 },
      { k: 'num', key: 'hi', label: 'to', dflt: 1, min: -1, max: 1, step: 0.05 },
      { k: 'choice', key: 'canny', label: '', dflt: 'any', options: [['any', 'any points'], ['on', 'on a Canny edge'], ['off', 'off Canny edges']] },
    ],
  },
  colour: {
    label: 'Colour', title: 'points by the hue, saturation and value of their colour (hue round through red when from > to)', combines: true, fields: [
      { k: 'num', key: 'h0', label: 'hue', dflt: 0, min: 0, max: 360, step: 5 },
      { k: 'num', key: 'h1', label: 'to', dflt: 360, min: 0, max: 360, step: 5, unit: '°' },
      { k: 'num', key: 's0', label: 'sat', dflt: 0, min: 0, max: 1, step: 0.05 },
      { k: 'num', key: 's1', label: 'to', dflt: 1, min: 0, max: 1, step: 0.05 },
      { k: 'num', key: 'v0', label: 'value', dflt: 0, min: 0, max: 1, step: 0.05 },
      { k: 'num', key: 'v1', label: 'to', dflt: 1, min: 0, max: 1, step: 0.05 },
    ],
  },
  density: {
    label: 'Density', title: 'points by how many points (of the ticked scans) are within a distance of each: low for stray ones, high where scans overlap', combines: true, fields: [
      { k: 'choice', key: 'r', label: 'within', dflt: '0.05', options: [['0.02', '2 cm'], ['0.05', '5 cm'], ['0.1', '10 cm'], ['0.2', '20 cm']] },
      { k: 'num', key: 'lo', label: '', dflt: 0, min: 0, int: true },
      { k: 'num', key: 'hi', label: 'to', dflt: 20, min: 0, int: true, unit: 'points' },
    ],
  },
  view: {
    label: 'View', title: 'points by the angle between the camera\'s forward direction (as it is when the macro runs) and their normal: 180° faces the camera squarely, 90° is edge on, 0° faces away', combines: true, fields: [
      { k: 'num', key: 'at', label: 'camera ray ∠ normal', dflt: 0, min: 0, max: 180, step: 5 },
      { k: 'num', key: 'spread', label: '±', dflt: 10, min: 0, max: 180, step: 5, unit: '°' },
    ],
  },
  frames: {
    label: 'Frames', title: 'points seen in a range of source frames (each scan numbers its own)', combines: true, fields: [
      { k: 'num', key: 'a', label: '', dflt: 1, min: 1, int: true },
      { k: 'num', key: 'b', label: 'to', dflt: 1, min: 1, int: true },
    ],
  },
  few: {
    label: 'Few named points', title: 'every point of the frames tied to another frame by fewer named points than this', combines: true, fields: [
      { k: 'num', key: 'n', label: 'fewer than', dflt: 3, min: 1, int: true },
    ],
  },
  features: {
    label: 'Photo features', title: 'find corners and straight lines in the frames\' photos (the frames of the selected points, or every frame shown), kept to those points\' pixels; later photo steps use them, and a camera\'s menu shows them on its photo', combines: false, fields: [
      { k: 'choice', key: 'on', label: 'on', dflt: 'sel', options: [['sel', 'the selected points'], ['all', 'whole photos of the frames shown']] },
      { k: 'choice', key: 'what', label: '', dflt: 'both', options: [['both', 'corners and lines'], ['corners', 'corners'], ['lines', 'lines']] },
      { k: 'num', key: 'n', label: 'at most', dflt: 400, min: 10, max: 5000, int: true, unit: 'corners' },
      { k: 'num', key: 'len', label: 'lines from', dflt: 20, min: 5, max: 400, int: true, unit: 'px' },
    ],
  },
  calibrate: {
    label: 'Calibrate lens', title: 'measure each scan\'s lens distortion from how much the long straight edges in its photos (Photo features first) bend, and use it for the photo steps after it (the points stay where they are)', combines: false, fields: [
      { k: 'num', key: 'len', label: 'edges from', dflt: 80, min: 30, max: 1000, int: true, unit: 'px' },
      { k: 'bool', key: 'k2', label: 'fit k2 too', dflt: true },
      { k: 'bool', key: 'use', label: 'use it', dflt: true },
    ],
  },
  alignCameras: {
    label: 'Align cameras', title: 'line the frames\' cameras up from their photos\' corners and lines matched across frames (Photo features first), each frame\'s points moving with its camera; shown moving as it goes, then one edit (Finish now stops it where it has got to)', combines: false, fields: [
      { k: 'choice', key: 'frames', label: '', dflt: 'sel', options: [['sel', 'the selected points\' frames'], ['shown', 'every frame with features']] },
      { k: 'choice', key: 'use', label: 'from', dflt: 'both', options: [['both', 'corners and lines'], ['corners', 'corners'], ['lines', 'lines']] },
      { k: 'num', key: 'iters', label: 'at most', dflt: 30, min: 1, max: 200, int: true, unit: 'rounds' },
      { k: 'num', key: 'depth', label: 'depth weight', dflt: 1, min: 0, max: 10, step: 0.1 },
      { k: 'num', key: 'reach', label: 'springs', dflt: 10, min: 0, max: 100, int: true, unit: 'links' },
      { k: 'num', key: 'max', label: 'moving at most', dflt: 0.1, min: 0.005, max: 1, step: 0.01, unit: 'm' },
      { k: 'num', key: 'offset', label: 'photos off their depth by up to', dflt: 0, min: 0, max: 20, step: 0.5, unit: 'px' },
      { k: 'bool', key: 'rematch', label: 'match again once closer', dflt: true },
      { k: 'choice', key: 'keep', label: '', dflt: 'better', options: [['better', 'keep it only if RTAB-Map\'s own matches (prep/features.py) agree better (without them: keep it)'], ['always', 'keep it']] },
    ],
  },
  matchEdges: {
    label: 'Match edges', title: 'compare each frame\'s depth edges (jumps, creases) with its photo\'s straight edges (Photo features first): says how far out of register depth and photo are, and selects the points on depth edges a photo edge agrees with', combines: true, fields: [
      { k: 'choice', key: 'kind', label: '', dflt: 'any', options: [['any', 'jumps and creases'], ['jump', 'depth jumps'], ['crease', 'creases']] },
      { k: 'num', key: 'tol', label: 'within', dflt: 3, min: 0.5, max: 20, step: 0.5, unit: 'px' },
      { k: 'num', key: 'search', label: 'searching', dflt: 30, min: 2, max: 100, int: true, unit: 'px' },
    ],
  },
  mesh: {
    label: 'Mesh export', title: 'build a mesh of the shown scans from their frames\' depth, as the frames are placed now (only the shown points\' depth: deleted and hidden points are left out), colour it from the photos, and download it; or keep it as one part of the next Mesh export\'s file (that part\'s space is then left to it: e.g. a corner at 1 cm, then the rest at 3 cm)', combines: false, fields: [
      { k: 'choice', key: 'engine', label: 'by', dflt: 'tsdf', options: Object.entries(ENGINE_FIELDS).map(([k, e]) => [k, e.label] as [string, string]) },
      { k: 'choice', key: 'region', label: 'of', dflt: 'all', options: [['all', 'everything shown'], ['sel', 'near the selected points (within 5-10 cm)'], ['vol', 'a volume box']] },
      { k: 'vol', key: 'vol', label: 'in', dflt: '*', on: ['region', ['vol']] },
      ...engineFields(),
      { k: 'choice', key: 'colour', label: 'coloured', dflt: 'blend', options: [['blend', 'by a blend of the photos that see it best'], ['best', 'by the photo that sees it best'], ['none', 'not at all']] },
      { k: 'bool', key: 'gains', label: 'matching the photos\' exposure', dflt: true },
      { k: 'choice', key: 'then', label: 'then', dflt: 'download', options: [['download', 'download it (with any parts kept before)'], ['keep', 'keep it for the next Mesh export']] },
      { k: 'choice', key: 'format', label: 'as', dflt: 'glb', options: [['glb', 'glTF binary (.glb)'], ['ply', 'PLY'], ['obj', 'OBJ'], ['fbx', 'FBX']], on: ['then', ['download']] },
    ],
  },
  edges3d: {
    label: '3D edges', title: 'match the photos\' lines across frames (Photo features first), triangulate the straight edges seen in enough photos, and select the points near them; the edges are drawn in the view', combines: true, fields: [
      { k: 'num', key: 'r', label: 'points within', dflt: 0.02, min: 0.002, max: 0.2, step: 0.005, unit: 'm' },
      { k: 'num', key: 'views', label: 'seen in at least', dflt: 3, min: 2, max: 20, int: true, unit: 'photos' },
    ],
  },
  points: { label: 'Points', title: 'floor, solid, a class, an object, a part, a group, a scan…', combines: true, fields: [{ k: 'src', key: 'src', label: '', dflt: 'all' }] },
  invert: { label: 'Invert', title: 'every shown point not selected, and none of those selected', combines: false, fields: [] },
  erode: { label: 'Erode', title: 'drop every selected point within this distance of a shown unselected one', combines: false, fields: [{ k: 'num', key: 'r', label: 'by', dflt: 0.05, min: 0.005, max: 1, step: 0.01, unit: 'm' }] },
  dilate: { label: 'Dilate', title: 'add every shown point within this distance of a selected one', combines: false, fields: [{ k: 'num', key: 'r', label: 'by', dflt: 0.05, min: 0.005, max: 1, step: 0.01, unit: 'm' }] },
  widenFrames: { label: '→Frames', title: 'widen the selection to every point of the frames it touches', combines: false, fields: [] },
  widenScan: { label: '→Scan', title: 'widen the selection to every point of the scans it touches', combines: false, fields: [] },
  clear: { label: 'Clear', title: 'select nothing', combines: false, fields: [] },
  group: { label: 'Group', title: 'points in a point group and the groups inside it (a group that isn\'t there: none)', combines: true, fields: [{ k: 'group', key: 'name', label: '', dflt: 'group' }] },
  groupEdit: {
    label: 'Group edit', title: 'add the selection to a memory group (out of any other), take it out, or empty the group; a group that isn\'t there is made. Memory groups last until the page is closed: Group save writes one to the scan', combines: false, fields: [
      { k: 'choice', key: 'act', label: '', dflt: 'add', options: [['add', 'add the selection to'], ['remove', 'take the selection out of'], ['clear', 'empty']] },
      { k: 'group', key: 'name', label: '', dflt: 'group' },
    ],
  },
  groupSave: {
    label: 'Group save', title: 'write a memory group to the scan as a saved group (replacing what that group holds; its points leave the other saved groups); the memory group goes', combines: false, fields: [
      { k: 'group', key: 'name', label: '', dflt: 'group' },
      { k: 'group', key: 'as', label: 'as', dflt: '', blank: 'same name' },
    ],
  },
  plane: {
    label: 'Plane', title: 'fit a construction plane to the selection as it is at this step (a new one, or one fitted again by name); kept in the frame most of the points came from', combines: false, fields: [
      { k: 'group', key: 'name', label: '', dflt: '', blank: 'next free name' },
    ],
  },
  weight: {
    label: 'Set weight', title: 'change the selected points\' weights: how much each one\'s photo counts where the photos are blended (Image weights shading; 1 unless set). Saved with the scan', combines: false, fields: [
      { k: 'choice', key: 'op', label: '', dflt: 'set', options: [['set', '='], ['add', '+='], ['sub', '−='], ['mul', '×=']] },
      { k: 'num', key: 'x', label: '', dflt: 1, min: 0, step: 0.1 },
      { k: 'data', key: 'of', label: 'or data group', dflt: '', blank: '(the number)' },
    ],
  },
  weightBlur: {
    label: 'Blur weights', title: 'blur the selected points\' weights (every shown point\'s, with none selected) across each frame\'s depth image: a point takes the average of its frame\'s points within r depth pixels, nearer ones counting more; pixels with no depth reading are left out', combines: false, fields: [
      { k: 'num', key: 'r', label: 'by', dflt: 3, min: 1, max: 20, int: true, unit: 'px' },
    ],
  },
  dataNew: {
    label: 'Data group', title: 'make a data group (a number per point) of the selected points, each its value from a source (by nearness, the shown points within r of the source points too); points not in it read as the default. Run again, it replaces the group. Data groups last until the page is closed, like memory groups', combines: false, fields: [
      { k: 'data', key: 'name', label: '', dflt: 'data' },
      { k: 'num', key: 'dflt', label: 'default', dflt: 0, step: 0.1 },
      ...srcFields(),
    ],
  },
  planeDetect: {
    label: 'Plane detector', title: 'make a data group of the selected points, each its likelihood (0 to 1) of lying on a plane at any slant (floor, wall, ramp, tilted board): 1 where the points within r lie flat (to within the flatness) and spread both ways, their normals agree with it (to within the angle) and the point sits on it; low at corners, curves, rods and clutter. Scans whose frames sit a little apart make planes look thick: loosen the flatness. A point with too few others near it reads as the default. Run again, it replaces the group', combines: false, fields: [
      { k: 'data', key: 'name', label: '', dflt: 'plane' },
      { k: 'num', key: 'dflt', label: 'default', dflt: 0, step: 0.1 },
      { k: 'num', key: 'r', label: 'within', dflt: 0.1, min: 0.005, max: 5, step: 0.01, unit: 'm' },
      ...planeFields(),
    ],
  },
  dataOp: {
    label: 'Data math', title: 'change a data group\'s values (of its points; of those selected too when ticked) by a source: a number, a point measure, another data group (its default where a point isn\'t in it), the nearness of other points, 1 touching to 0 at r and further (the falloff curve between; a data group\'s points each count by their value, to at most 1), or the likelihood of lying on a plane (as Plane detector). A point whose source is unknown keeps its value', combines: false, fields: [
      { k: 'data', key: 'name', label: '', dflt: 'data' },
      { k: 'choice', key: 'op', label: '', dflt: 'mul', options: [['set', '='], ['add', '+='], ['sub', '−='], ['mul', '×='], ['div', '÷='], ['min', 'min='], ['max', 'max=']] },
      ...srcFields(),
      { k: 'bool', key: 'sel', label: 'selected points only', dflt: false },
    ],
  },
  dataEval: {
    label: 'Data eval', title: `make (or replace) a data group from others, point by point: numbers, data group names ([in brackets] with spaces), + - * / %, brackets, and the points' own ${Object.keys(MEASURES).join(', ')} (where no data group has the name). Its points: those of the groups it reads (each its default where a point isn't in it); reading none, the selected points. With += (-= *= /= %=), the group's own points change, as Data math does`, combines: false, fields: [
      { k: 'data', key: 'name', label: '', dflt: 'data' },
      { k: 'choice', key: 'op', label: '', dflt: '=', options: [['=', '='], ['+', '+='], ['-', '−='], ['*', '×='], ['/', '÷='], ['%', '%=']] },
      { k: 'text', key: 'expr', label: '', dflt: 'a * b', placeholder: 'a * b + 1' },
    ],
  },
  dataEdit: {
    label: 'Data edit', title: 'add the selected points to a data group (at its default), take them out, or delete the group', combines: false, fields: [
      { k: 'choice', key: 'act', label: '', dflt: 'add', options: [['add', 'add the selection to'], ['remove', 'take the selection out of'], ['delete', 'delete']] },
      { k: 'data', key: 'name', label: '', dflt: 'data' },
    ],
  },
  dataBand: {
    label: 'Data band', title: 'points of a data group whose value is from lo to hi (points not in it too when ticked, at its default)', combines: true, fields: [
      { k: 'data', key: 'name', label: '', dflt: 'data' },
      { k: 'num', key: 'lo', label: '', dflt: 0.5, step: 0.05 },
      { k: 'num', key: 'hi', label: 'to', dflt: 1, step: 0.05 },
      { k: 'bool', key: 'nulls', label: 'points not in it too', dflt: false },
    ],
  },
  macro: { label: 'Macro', title: 'run another saved macro\'s steps here (with their own modes)', combines: false, fields: [{ k: 'macro', key: 'name', label: '', dflt: '' }] },
  blobs: {
    label: 'For each', title: 'run the steps inside once for each of: the blobs of the shown selection (selected points within reach of each other, flood filled; biggest first; those with fewer or more points than these dropped), the frames it has points of, the scans it has points of (each as the selection in turn: those of its points; the selection after is what they left selected, every one\'s together), or a number of times (each starting from what the one before left). Its variable, <name> in the inputs of the steps inside, is the frame\'s number for frames, else which time round it is, from 1. With no steps inside it selects what it would go over (say, every blob of that many points)', combines: false, fields: [
      { k: 'choice', key: 'over', label: '', dflt: 'blobs', options: FOR_EACH },
      { k: 'num', key: 'reach', label: 'within', dflt: 0.05, min: 0.005, max: 1, step: 0.01, unit: 'm', on: ['over', ['blobs']] },
      { k: 'num', key: 'min', label: 'of', dflt: 50, min: 1, int: true, unit: 'points', on: ['over', ['blobs']] },
      { k: 'num', key: 'max', label: 'to', dflt: 0, min: 0, int: true, unit: 'points', blank: 'any', on: ['over', ['blobs']] },
      { k: 'num', key: 'most', label: 'at most', dflt: 100, min: 0, int: true, blank: 'all', on: ['over', ['blobs', 'frames', 'scans']] },
      { k: 'num', key: 'n', label: '1 to', dflt: 3, min: 1, max: MAX_LOOP, int: true, on: ['over', ['count']] },
      { k: 'var', key: 'var', label: 'as', dflt: 'i' },
    ],
  },
  tune: {
    label: 'Auto tune', title: 'find the bounds of the ticked selection rules (all of them ∩, each starting from letting everything through, each maybe inverted) that select every point of its require list and none of its deny list, or come as near as they can (the best average of the two). Each pass tunes them again ∩ what the pass before left; after each, it tries erode then dilate, and dilate then erode (by up to the distance), keeping one that does better. Shown as it goes, until it gets there, runs out of rounds or Finish. Its + / − buttons put the selection in or take it out of the lists, saved with the macro', combines: true, fields: [
      { k: 'num', key: 'iters', label: 'at most', dflt: 300, min: 1, max: 100000, int: true, unit: 'rounds a pass' },
      { k: 'num', key: 'passes', label: 'in', dflt: 1, min: 1, max: 8, int: true, unit: 'passes' },
      { k: 'bool', key: 'morph', label: 'erode/dilate after each', dflt: true },
      { k: 'num', key: 'mr', label: 'by up to', dflt: 0.05, min: 0.005, max: 1, step: 0.005, unit: 'm' },
      { k: 'bool', key: 'normal', label: 'Normal', dflt: true },
      { k: 'bool', key: 'band', label: 'Band', dflt: true },
      { k: 'bool', key: 'distance', label: 'Distance', dflt: true },
      { k: 'bool', key: 'edges', label: 'Edges', dflt: true },
      { k: 'bool', key: 'colour', label: 'Colour', dflt: true },
      { k: 'bool', key: 'density', label: 'Density', dflt: true },
      { k: 'choice', key: 'r', label: 'within', dflt: '0.05', options: [['0.02', '2 cm'], ['0.05', '5 cm'], ['0.1', '10 cm'], ['0.2', '20 cm']] },
      { k: 'bool', key: 'view', label: 'View', dflt: true },
      { k: 'bool', key: 'frames', label: 'Frames', dflt: true },
      { k: 'bool', key: 'few', label: 'Few named points', dflt: true },
      { k: 'data', key: 'data', label: 'Data band of', dflt: '', blank: '(none)' },
    ],
  },
  keep: {
    label: 'Keep', title: 'hold the selection as a node\'s output (a node graph\'s step: the selection stays)', combines: false, hidden: true, fields: [
      { k: 'slot', key: 'slot', label: '', dflt: 'n1' },
    ],
  },
  recall: {
    label: 'Recall', title: 'a node\'s output (held by Keep) combined into the selection (a node graph\'s step)', combines: true, hidden: true, fields: [
      { k: 'slot', key: 'slot', label: '', dflt: 'n1' },
    ],
  },
  dataCopy: {
    label: 'Data copy', title: 'a copy of a data group under another name (a node graph\'s step)', combines: false, hidden: true, fields: [
      { k: 'data', key: 'name', label: '', dflt: 'data' },
      { k: 'data', key: 'of', label: 'of', dflt: 'data' },
    ],
  },
} satisfies Record<string, Kind>;

export type StepKind = keyof typeof KINDS;

/** Rules that can be inverted (select the points they wouldn't, of those that may be selected): every one that combines with a mode but Auto tune, which inverts its own. */
export const invertible = (t: StepKind) => KINDS[t].combines && t !== 'tune' && t !== 'recall';
export const INV: Field = { k: 'bool', key: 'inv', label: 'invert', dflt: false };
for (const t of Object.keys(KINDS) as StepKind[]) if (invertible(t)) (KINDS[t] as Kind).fields.push(INV);
export type Params = Record<string, number | string | boolean>;
/** An Auto tune step's lists, per scan id: the points it must select (req) and must not (deny); best: what it found last. */
export interface TuneData { sets: Record<string, { req?: PidSet; deny?: PidSet }>; best?: Step[] }
/**
 * off: unticked in the dialog, so a run skips it (kept, to tick again); tune: an Auto tune step's lists; args: a Macro
 * step's values for that macro's variables (a value, or <name>: the caller's), the rest left at its defaults; steps:
 * a For each step's own steps, run once a time round; in a run (flatten's, never saved): body, how many of the run
 * steps after a For each step are its own, and loop, the number its variable's token has (<#loop>).
 */
export interface Step { t: StepKind; mode?: Combine; p: Params; off?: true; tune?: TuneData; args?: Params; steps?: Step[]; body?: number; loop?: number }
/** vars: the default value of each variable (<name>) its steps use; graph: a node graph's (nodes.ts), its steps none. */
export interface Macro { name: string; steps: Step[]; vars?: Params; graph?: Graph }

export const MODES: [Combine, string][] = [['replace', 'New'], ['add', '+'], ['sub', '−'], ['inter', '∩']];
export const MAX_MACROS = 200, MAX_STEPS = 100;
/** The rules Auto tune bounds (its best steps are of these), and the most it may save: steps, point set bytes. */
export const TUNABLE = new Set<StepKind>(['normal', 'band', 'distance', 'edges', 'colour', 'density', 'view', 'frames', 'few', 'dataBand']);
/** Steps Auto tune writes out besides those: the erode / dilate after a pass. */
export const TUNE_MORPH = new Set<StepKind>(['erode', 'dilate']);
export const MAX_BEST = 160, MAX_TUNE_B64 = 8 << 20;
/** A run's limits: macros inside macros this deep, and this many steps in all. */
export const MAX_DEPTH = 8, MAX_RUN = 1000;
/** Steps that change groups or frames, so run through the page (the rest only change the selection). */
export const EDITS = new Set<StepKind>(['groupEdit', 'groupSave', 'plane', 'alignCameras', 'weight', 'weightBlur']);
/** Steps that change the points' weights (weights.ts): the page does them and saves the scans' weights after the run. */
export const WEIGHTS = new Set<StepKind>(['weight', 'weightBlur']);
/** Of those, the steps that commit edits to the scan (Group edit changes memory groups only; weights save apart). */
export const SAVES = new Set<StepKind>(['groupSave', 'plane', 'alignCameras']);
/** Steps that run on the frames' photos: the page does them (its photo pool), the run waits ('photo'). */
export const PHOTO = new Set<StepKind>(['features', 'calibrate', 'alignCameras', 'matchEdges', 'edges3d', 'mesh']);
/** Photo steps that then select, from what the page found (ctx.photo). */
export const PHOTO_SEL = new Set<StepKind>(['matchEdges', 'edges3d']);
/** The step runs through the page (EDITS). */
export const edits = (s: Step) => EDITS.has(s.t);
/** The step commits edits to the scan when it runs (SAVES). */
export const commits = (s: Step) => SAVES.has(s.t);
const NAME = /^[^\0-\x1f]{1,60}$/;
/** A variable in a step's input: <name> (the whole input), and a variable's name; the most a macro or step has. */
export const VAR = /^<([A-Za-z_]\w{0,39})>$/, VAR_NAME = /^[A-Za-z_]\w{0,39}$/, MAX_VARS = 50;
/** A Keep / Recall slot's name: a graph run's prefix, a node's id, maybe a port's name. */
const SLOT = /^[A-Za-z_]\w{0,119}$/;
/** The variable an input is (<name>), or null: a value. */
export const varOf = (v: unknown): string | null => (typeof v === 'string' ? VAR.exec(v)?.[1] ?? null : null);
/** Math in a step's input, <i+1> or <w*h> (a bare <name> is one too), parsed; null when the input isn't one. */
export function exprOf(v: unknown): Expr | null {
  const m = typeof v === 'string' ? /^<([^<>#]{1,200})>$/.exec(v) : null;
  return m ? parseExpr(m[1]) : null;
}
/** A For each variable in a run step's input (flatten's, never saved): <#j>, j the For each step's loop; or math over
 *  them, <#=#j+1> (the macro's variables already numbers). */
const LOOP_TOK = /^<#(\d+)>$/, LOOP_EXPR = /^<#=([^<>]{1,400})>$/;
const loopOf = (v: unknown): number | null => (typeof v === 'string' && LOOP_TOK.test(v) ? +v.slice(2, -1) : null);
/** A loop token as math over For each refs (<#j>: just #j), or null when v isn't one. */
function loopExpr(v: unknown): Expr | null {
  const j = loopOf(v);
  if (j !== null) return { loop: j };
  const m = typeof v === 'string' ? LOOP_EXPR.exec(v) : null;
  return m ? parseExpr(m[1]) : null;
}

export const kindOf = (t: string): Kind | undefined => (Object.hasOwn(KINDS, t) ? (KINDS as Record<string, Kind>)[t] : undefined);

/** A new step of kind t: its defaults, overridden by `from` (say, the toolbar's current values) where those fit. */
export function newStep(t: StepKind, from: Params = {}, mode: Combine = 'replace'): Step {
  const k: Kind = KINDS[t], p: Params = {};
  for (const f of k.fields) p[f.key] = fieldValue(f, from[f.key]) ?? f.dflt;
  return k.combines ? { t, mode, p } : t === 'blobs' ? { t, p, steps: [] } : { t, p };
}

/** v as field f takes it, or undefined when it doesn't fit. */
export function fieldValue(f: Field, v: unknown): number | string | boolean | undefined {
  switch (f.k) {
    case 'num': {
      if (typeof v !== 'number' || !Number.isFinite(v)) return undefined;
      if ((f.min !== undefined && v < f.min) || (f.max !== undefined && v > f.max)) return undefined;
      return f.int ? Math.round(v) : v;
    }
    case 'choice': return typeof v === 'string' && f.options.some(([o]) => o === v) ? v : undefined;
    case 'bool': return typeof v === 'boolean' ? v : undefined;
    case 'group': case 'data': return typeof v === 'string' && (GROUP_NAME.test(v) || (v === '' && f.blank !== undefined)) ? v : undefined;
    case 'macro': return typeof v === 'string' && (v === '' || NAME.test(v)) ? v : undefined;   // '': not picked yet (a run stops there)
    case 'var': return typeof v === 'string' && VAR_NAME.test(v) ? v : undefined;
    case 'slot': return typeof v === 'string' && SLOT.test(v) ? v : undefined;
    default: return typeof v === 'string' && v.length <= 200 ? v : undefined;
  }
}

/** Field f's input can be math (<i+1>): it can take a number (written out for a name). */
const mathOk = (f: Field) => f.k !== 'var' && f.k !== 'slot' && f.k !== 'bool' && f.k !== 'macro' && f.k !== 'choice';

/** v as a step's input takes it: a variable (<name>), math (<i+1>) where a number fits, or a value field f takes; undefined when none. */
export const paramValue = (f: Field, v: unknown) => ((varOf(v) && f.k !== 'var') || (mathOk(f) && exprOf(v)) ? (v as string) : fieldValue(f, v));

/** What field f takes, for messages: "takes 0 to 360". */
export function takes(f: Field): string {
  switch (f.k) {
    case 'num': return `takes${f.min !== undefined ? ` ${f.min}` : ''}${f.max !== undefined ? ` to ${f.max}` : f.min !== undefined ? ' or more' : ' a number'}${f.int ? ' (whole)' : ''}`;
    case 'choice': return `takes one of ${f.options.map(([o]) => o).join(', ')}`;
    case 'bool': return 'takes true or false';
    case 'group': return 'takes a group name: letters, digits, space . : + -';
    case 'data': return 'takes a data group name: letters, digits, space . : + -';
    case 'macro': return 'takes a macro\'s name';
    case 'var': return 'takes a variable name: letters, digits and _';
    case 'slot': return 'takes a slot name: letters, digits and _';
    case 'text': return 'takes up to 200 characters';
    default: return 'takes a name';
  }
}

/** A checked copy of a macro's defaults or a Macro step's args (variable name → value; args may pass <name> on). */
function parseVals(raw: unknown, what: string, refs: boolean): Params {
  if (raw === undefined) return {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error(`${what}: want variable → value`);
  const e = Object.entries(raw as Record<string, unknown>);
  if (e.length > MAX_VARS) throw new Error(`${what}: ${MAX_VARS} variables at most`);
  const out: Params = {};
  for (const [k, v] of e) {
    if (!VAR_NAME.test(k)) throw new Error(`${what}: bad variable name ${JSON.stringify(k)}`);
    const ok = typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && v.length <= 200 && (refs || !exprOf(v)));
    if (!ok) throw new Error(`${what}: bad value for ${k} ${JSON.stringify(v)}`);
    out[k] = v as number | string | boolean;
  }
  return out;
}

/** A step's parameters as saved by an older version, brought up to date: Edges from before it had a side (it was the
 *  either-side strength); View from when it took a band lo to hi of normal · (direction to the eye), -1 to 1, or a0 to
 *  a1 degrees from the camera's forward direction (180 facing), as the band's middle and half width in degrees to the
 *  nearest 0.1° (its "either side" is dropped: the facing side is kept). */
function upgrade(t: unknown, rp: Record<string, unknown>): Record<string, unknown> {
  const r = upgradeOld(t, rp);
  if (typeof t === 'string' && kindOf(t) && invertible(t as StepKind) && r.inv === undefined) return { ...r, inv: false };   // from before rules could be inverted
  if (t === 'tune') { const o = { ...r }; for (const f of KINDS.tune.fields as Field[]) if (o[f.key] === undefined && ['passes', 'morph', 'mr', 'data'].includes(f.key)) o[f.key] = f.dflt; return o; }
  if ((t === 'dataNew' || t === 'dataOp') && r.tol === undefined) return { ...r, tol: 0.03, deg: 20 };   // from before the planar source
  if (t === 'blobs' && r.over === undefined) { const o = { ...r }; for (const f of KINDS.blobs.fields as Field[]) o[f.key] ??= f.dflt; return o; }   // from when it was Each blob
  return r;
}
function upgradeOld(t: unknown, rp: Record<string, unknown>): Record<string, unknown> {
  if (t === 'edges' && rp.side === undefined) return { ...rp, side: 'abs' };
  if (t === 'weight' && rp.of === undefined) return { ...rp, of: '' };   // from before it could take a data group's values
  if (t === 'features' && rp.on === 'planes') return { ...rp, on: 'sel' };   // from when Planes steps found planes for it
  if (t !== 'view' || rp.at !== undefined) return rp;
  const num = (k: string) => (typeof rp[k] === 'number' ? (rp[k] as number) : NaN), d1 = (x: number) => Math.round(x * 10) / 10;
  const deg = (d: number) => (Math.acos(d) * 180) / Math.PI;
  const [x, y] = Number.isFinite(num('a0')) ? [180 - num('a0'), 180 - num('a1')] : Math.abs(num('lo')) <= 1 && Math.abs(num('hi')) <= 1 ? [deg(num('lo')), deg(num('hi'))] : [NaN, NaN];
  if (!Number.isFinite(x) || !Number.isFinite(y)) return rp;
  return { at: d1((x + y) / 2), spread: d1(Math.abs(x - y) / 2) };
}

/** A checked copy of a step (depth: inside this many For each steps); throws on anything it can't take. */
export function parseStep(raw: unknown, depth = 0): Step {
  const r = raw as { t?: unknown; mode?: unknown; p?: unknown; off?: unknown } | null;
  const k = typeof r?.t === 'string' ? kindOf(r.t) : undefined;
  if (!r || !k || k.hidden) throw new Error(`unknown step ${JSON.stringify(r?.t)}`);   // a node graph's own steps aren't saved
  const p: Params = {}, rp = upgrade(r.t, (r.p ?? {}) as Record<string, unknown>);
  for (const f of k.fields) {
    const v = paramValue(f, rp[f.key]);
    if (v === undefined) throw new Error(`${k.label}: bad ${f.key} ${JSON.stringify(rp[f.key])}`);
    p[f.key] = v;
  }
  const off = r.off === true ? { off: true as const } : {};
  const tune = r.t === 'tune' && (r as { tune?: unknown }).tune !== undefined ? { tune: parseTune((r as { tune?: unknown }).tune) } : {};
  const args = r.t === 'macro' && (r as { args?: unknown }).args !== undefined ? parseVals((r as { args?: unknown }).args, `${k.label} ${p.name}`, true) : {};
  if (r.t === 'blobs') return { t: 'blobs', p, ...off, steps: parseBody((r as { steps?: unknown }).steps, depth) };
  if (!k.combines) return { t: r.t as StepKind, p, ...off, ...(Object.keys(args).length ? { args } : {}) };
  if (!MODES.some(([m]) => m === r.mode)) throw new Error(`${k.label}: bad mode ${JSON.stringify(r.mode)}`);
  return { t: r.t as StepKind, mode: r.mode as Combine, p, ...off, ...tune };
}

/** A checked copy of a For each step's own steps (none yet is fine; no Auto tune: its lists and tools are the macro's own steps'). */
function parseBody(raw: unknown, depth: number): Step[] {
  if (raw === undefined) return [];
  if (!Array.isArray(raw) || raw.length > MAX_STEPS) throw new Error(`For each: want up to ${MAX_STEPS} steps inside`);
  if (depth >= MAX_DEPTH) throw new Error(`For each: nested more than ${MAX_DEPTH} deep`);
  return raw.map(x => {
    const s = parseStep(x, depth + 1);
    if (s.t === 'tune') throw new Error('For each: Auto tune can\'t go inside it');
    return s;
  });
}

const SCAN_ID = /^[^\0-\x1f]{1,200}$/, B64 = /^[A-Za-z0-9+/]*={0,2}$/;

/** A checked copy of an Auto tune step's lists; throws on anything it can't take. */
function parseTune(raw: unknown): TuneData {
  const r = raw as { sets?: unknown; best?: unknown } | null;
  if (!r || typeof r !== 'object' || !r.sets || typeof r.sets !== 'object' || Array.isArray(r.sets)) throw new Error('Auto tune: bad lists');
  const sets: TuneData['sets'] = {};
  let bytes = 0;
  const pid = (x: unknown): PidSet => {
    const q = x as PidSet | null;
    if (!q || q.enc !== 'rle1' || !Number.isInteger(q.n) || q.n < 0 || typeof q.b64 !== 'string' || !B64.test(q.b64)) throw new Error('Auto tune: bad point set');
    bytes += q.b64.length;
    if (bytes > MAX_TUNE_B64) throw new Error(`Auto tune: point lists over ${MAX_TUNE_B64 >> 20} MB`);
    return { enc: 'rle1', n: q.n, b64: q.b64 };
  };
  for (const [id, v] of Object.entries(r.sets as Record<string, unknown>)) {
    if (!SCAN_ID.test(id) || !v || typeof v !== 'object') throw new Error(`Auto tune: bad lists for ${JSON.stringify(id)}`);
    const { req, deny } = v as { req?: unknown; deny?: unknown }, o: { req?: PidSet; deny?: PidSet } = {};
    if (req !== undefined) o.req = pid(req);
    if (deny !== undefined) o.deny = pid(deny);
    sets[id] = o;
  }
  if (r.best === undefined) return { sets };
  if (!Array.isArray(r.best) || r.best.length > MAX_BEST) throw new Error(`Auto tune: want up to ${MAX_BEST} best steps`);
  const best = r.best.map(b => {
    const s = parseStep(b);
    if (!TUNABLE.has(s.t) && !TUNE_MORPH.has(s.t)) throw new Error(`Auto tune: a ${s.t} step isn't one it tunes`);
    return s;
  });
  return { sets, best };
}

/** An Auto tune step's list sizes over every scan. */
export function tuneCounts(s: Step): { req: number; deny: number } {
  let req = 0, deny = 0;
  for (const v of Object.values(s.tune?.sets ?? {})) { req += v.req?.n ?? 0; deny += v.deny?.n ?? 0; }
  return { req, deny };
}

/**
 * The points of mask (per vertex; pidOfV: each vertex's point id, of npid) put in list l of an Auto tune's lists for
 * scan (and out of the other), or taken out of it: how many points that moved.
 */
export function tuneListEdit(t: TuneData, scan: string, npid: number, pidOfV: ArrayLike<number>, mask: Uint8Array, l: 'req' | 'deny', act: 'add' | 'remove'): number {
  const o = l === 'req' ? 'deny' : 'req', e = { ...t.sets[scan] };
  const flags = (x?: PidSet) => { const f = new Uint8Array(npid); if (x?.n) for (const p of decodeSet(x, npid)) f[p] = 1; return f; };
  const a = flags(e[l]), b = flags(e[o]);
  let n = 0;
  for (let v = 0; v < mask.length; v++) if (mask[v]) {
    const p = pidOfV[v];
    if (act === 'add') { n += a[p] ^ 1; a[p] = 1; b[p] = 0; } else { n += a[p]; a[p] = 0; }
  }
  const put = (k: 'req' | 'deny', f: Uint8Array) => { const x = encodeSet(maskToPids(f)); if (x.n) e[k] = x; else delete e[k]; };
  put(l, a); put(o, b);
  if (e.req || e.deny) t.sets[scan] = e; else delete t.sets[scan];
  return n;
}

/** Steps there are no more, with what to say of them: saved macros lose them on loading (dropped). */
export const GONE: Record<string, string> = { planes: 'Planes is gone (Plane detector gives each point its likelihood of lying on a plane)' };

/**
 * Saved macros (the scene's macros.json, as it is) without the steps there are no more, at any depth, and what was
 * dropped (one line per macro). The file keeps them until a macro is saved again.
 */
export function dropGone(raw: Macro[]): { macros: Macro[]; dropped: string[] } {
  const dropped: string[] = [];
  const keep = (steps: Step[], name: string, gone: Set<string>): Step[] =>
    steps.filter(s => !(Object.hasOwn(GONE, s.t) && gone.add(s.t))).map(s => (s.steps ? { ...s, steps: keep(s.steps, name, gone) } : s));
  const macros = raw.map(m => {
    if (!Array.isArray(m?.steps)) return m;
    const gone = new Set<string>(), steps = keep(m.steps, m.name, gone);
    if (!gone.size) return m;
    dropped.push(`${m.name}: ${[...gone].map(t => GONE[t]).join('; ')}`);
    return { ...m, steps };
  });
  return { macros, dropped };
}

/** A checked copy of a list of macros (names unique); throws on anything it can't take. */
export function parseMacros(raw: unknown): Macro[] {
  if (!Array.isArray(raw) || raw.length > MAX_MACROS) throw new Error(`want a list of up to ${MAX_MACROS} macros`);
  const seen = new Set<string>();
  const out = raw.map((m: { name?: unknown; steps?: unknown; vars?: unknown; graph?: unknown }) => {
    const name = typeof m?.name === 'string' ? m.name.trim() : '';
    if (!NAME.test(name)) throw new Error(`bad macro name ${JSON.stringify(m?.name)}`);
    if (seen.has(name)) throw new Error(`two macros called ${name}`);
    seen.add(name);
    if (!Array.isArray(m.steps) || m.steps.length > MAX_STEPS) throw new Error(`${name}: want up to ${MAX_STEPS} steps`);
    const vars = parseVals(m.vars, name, false);
    let graph: Graph | undefined;
    if (m.graph !== undefined) {
      try { graph = parseGraph(m.graph); } catch (e) { throw new Error(`${name}: ${(e as Error).message}`); }
      if (m.steps.length) throw new Error(`${name}: a node graph has no steps of its own`);
    }
    return { name, steps: m.steps.map(s => parseStep(s)), ...(Object.keys(vars).length ? { vars } : {}), ...(graph ? { graph } : {}) };
  });
  const loop = macroCycle(out);
  if (loop) throw new Error(`a macro runs itself: ${loop}`);
  return out;
}

/** Steps and the steps inside them (For each's), at any depth. */
export const allSteps = (steps: Step[]): Step[] => steps.flatMap(s => [s, ...(s.steps ? allSteps(s.steps) : [])]);

/** The names of the macros a macro's steps run (ticked or not; one named by a variable isn't known till it runs). */
const runs = (m: Macro) => (m.graph ? graphRuns(m.graph).filter(n => !varOf(n)) : allSteps(m.steps).filter(s => s.t === 'macro' && !varOf(s.p.name)).map(s => String(s.p.name)));

/** A macro's steps as its list has them, or a node graph's nodes as steps (nodes.ts nodeSteps: for its variables, not to run). */
export const stepsOf = (m: Macro): Step[] => (m.graph ? nodeSteps(m.graph) : m.steps);

/** How many steps running m takes at its own level (ticked steps; a graph: its nodes), 0: nothing to run. */
export const runSize = (m: Macro) => (m.graph ? m.graph.nodes.length : m.steps.filter(s => !s.off).length);

/** "a → b → a" when saved macros run each other in a loop, else null (a name not saved is no loop). */
export function macroCycle(macros: Macro[]): string | null {
  const by = new Map(macros.map(m => [m.name, m])), done = new Set<string>(), path: string[] = [];
  const visit = (n: string): string | null => {
    const i = path.indexOf(n);
    if (i >= 0) return [...path.slice(i), n].join(' → ');
    const m = by.get(n);
    if (!m || done.has(n)) return null;
    path.push(n);
    for (const r of runs(m)) { const c = visit(r); if (c) return c; }
    path.pop();
    done.add(n);
    return null;
  };
  for (const m of macros) { const c = visit(m.name); if (c) return c; }
  return null;
}

/**
 * A step a run takes, and where: its index in each macro down from the one run ([outer, inner, …]; in a node graph,
 * in the steps it compiles to); node: the node of the graph run it comes from (a Macro node's macro's steps: that node).
 */
export interface RunStep { s: Step; at: number[]; node?: string }

/** "step 3 › 2 (Normal …)", or "node n3 (Normal …)": where a run step is, for messages. */
export const whereIs = (r: RunStep) => (r.node ? `node ${r.node}` : `step ${r.at.map(i => i + 1).join(' › ')}`) + ` (${describe(r.s)})`;

/** Variable name → value, as a run has them. */
export type Env = Record<string, number | string | boolean>;

/** v as field f takes a For each's number (a name: the number written out), or undefined when it doesn't. */
const loopValue = (f: Field, v: number) => fieldValue(f, v) ?? (f.k === 'num' || f.k === 'bool' ? undefined : fieldValue(f, String(v)));

/**
 * s with its variables' values put in from env (as its fields take them; a Macro step's args left off); a For each's
 * variable stays its token (<#j>, the run puts the number in) where its input could take a number. Throws, saying where.
 */
/**
 * Math (not a bare <name>) worked out with env's values: a number, or when it uses a For each's variable a loop token
 * for the run (<#=…>, the rest put in as numbers). Throws, saying where, on a variable with no value or not a number.
 */
function mathValue(e: Expr, env: Env, where: string): number | string {
  const late = new Map<string, Expr>();
  for (const n of exprNames(e)) {
    if (!Object.hasOwn(env, n)) throw new Error(`${where}: no value for <${n}>`);
    const l = loopExpr(env[n]);
    if (l) late.set(n, l);
    else if (typeof env[n] !== 'number') throw new Error(`${where}: <${n}> is ${JSON.stringify(env[n])}, not a number`);
  }
  if (late.size) return `<#=${exprText(e, n => late.get(n) ?? { n: env[n] as number })}>`;
  try { return evalExpr(e, n => env[n as string] as number); } catch (err) { throw new Error(`${where}: ${(err as Error).message}`); }
}

function bind(s: Step, env: Env, where: string): Step {
  const k: Kind = KINDS[s.t], p: Params = {};
  for (const f of k.fields) {
    const v = s.p[f.key], n = varOf(v), e = n || !mathOk(f) ? null : exprOf(v);
    if (e) {
      const x = mathValue(e, env, where);
      if (typeof x === 'string') {
        if (loopValue(f, 1) === undefined) throw new Error(`${where}: ${v} is For each's number, ${f.label || f.key} ${takes(f)}`);
        p[f.key] = x;
        continue;
      }
      const y = loopValue(f, x);
      if (y === undefined) throw new Error(`${where}: ${v} is ${x}, ${f.label || f.key} ${takes(f)}`);
      p[f.key] = y;
      continue;
    }
    if (!n) { p[f.key] = v; continue; }
    if (!Object.hasOwn(env, n)) throw new Error(`${where}: no value for <${n}>`);
    if (loopExpr(env[n]) !== null) {
      if (f.k === 'macro') throw new Error(`${where}: the macro to run can't be For each's <${n}>`);
      if (loopValue(f, 1) === undefined) throw new Error(`${where}: <${n}> is For each's number, ${f.label || f.key} ${takes(f)}`);
      p[f.key] = env[n];
      continue;
    }
    const x = fieldValue(f, env[n]);
    if (x === undefined) throw new Error(`${where}: <${n}> is ${JSON.stringify(env[n])}, ${f.label || f.key} ${takes(f)}`);
    p[f.key] = x;
  }
  const { args: _, steps: __, ...rest } = s;
  return { ...rest, p };
}

/** A run step's For each tokens (<#j>), or none. */
export const loopsIn = (s: Step) => Object.values(s.p).some(v => loopExpr(v) !== null);

/** A run step with the For each numbers put in (vals: by loop, as the run is now); throws when one doesn't fit. */
export function bindLoop(s: Step, vals: Map<number, number>): Step {
  const p: Params = { ...s.p };
  for (const f of KINDS[s.t].fields as Field[]) {
    const e = loopExpr(p[f.key]);
    if (e === null) continue;
    if (exprLoops(e).some(j => !vals.has(j))) throw new Error(`For each's variable used outside it`);
    let v: number;
    try { v = evalExpr(e, j => vals.get(j as number)!); } catch (err) { throw new Error(`${f.label || f.key}: ${(err as Error).message}`); }
    const x = loopValue(f, v);
    if (x === undefined) throw new Error(`For each's number ${v} doesn't fit: ${f.label || f.key} ${takes(f)}`);
    p[f.key] = x;
  }
  return { ...s, p };
}

/**
 * The steps running m takes, in order: the ticked ones, a Macro step's macro (from `macros`, by name) put in its
 * place, with every variable's value put in: m's defaults (env over them), and in a Macro step's macro its own
 * defaults, the step's args over them. Throws, saying where, on a macro not saved or not picked, a macro that runs
 * itself, macros nested deeper than MAX_DEPTH, more than MAX_RUN steps in all, a variable with no value or one that
 * doesn't fit its input.
 */
export function flatten(m: Macro, macros: Macro[], maxRun = MAX_RUN, env: Env = {}): RunStep[] {
  const out: RunStep[] = [];
  let graphs = 0;
  /**
   * A macro's steps: its own, or its graph's compiled (each its node; slots of its own: pre); env: its named selection
   * ports' slots where no Sub Graph node passes them (an input: the selection it was given; an output: one of its own).
   */
  const stepsFor = (x: Macro, where: string): { steps: Step[]; node?: string[]; env: Env } => {
    if (!x.graph) return { steps: x.steps, env: {} };
    try {
      const pre = graphs++ ? `g${graphs}_` : '', c = compile(x.graph, pre), io = graphPorts(x.graph), env: Env = {};
      const sels = [...io.ins.map(p => ({ ...p, out: false })), ...io.outs.map(p => ({ ...p, out: true }))].filter(p => p.type === 'sel' && !p.result);
      const vars = sels.length ? varsOf(x, macros) : null;
      for (const p of sels) {
        if (vars!.has(p.name)) throw new Error(`<${p.name}> is a selection ${p.out ? 'output' : 'input'}'s name and a variable too`);
        env[p.name] = p.out ? `${pre}x_${p.name}` : `${pre}start`;
      }
      return { ...c, env };
    } catch (e) { throw new Error(`${where}: ${(e as Error).message}`); }
  };
  // node: the graph node each step of steps comes from (the top macro's graph), or the one they all do (inside it)
  const go = (steps: Step[], at: number[], stack: string[], env: Env, node?: string[] | string) => {
    steps.forEach((s0, i) => {
      if (s0.off) return;
      const here = [...at, i], nd = typeof node === 'string' ? node : node?.[i] || undefined, tag = nd ? { node: nd } : {};
      const s = bind(s0, env, whereIs({ s: s0, at: here, ...tag }));
      if (s.t !== 'macro') {
        if (out.length >= maxRun) throw new Error(`more than ${maxRun} steps in all`);
        out.push({ s, at: here, ...tag });
        if (s.t === 'blobs') {   // its steps right after it, as many as body says (the run goes over them once a time round)
          const start = out.length;
          s.loop = start - 1;
          go(s0.steps ?? [], here, stack, { ...env, [String(s.p.var)]: `<#${s.loop}>` }, nd);
          s.body = out.length - start;   // none: the parts it goes over are what it selects
        }
        return;
      }
      const where = whereIs({ s, at: here, ...tag }), name = String(s.p.name);
      if (!name) throw new Error(`${where}: pick the macro to run`);
      if (stack.includes(name)) throw new Error(`${where}: runs itself (${[...stack, name].join(' → ')})`);
      const sub = macros.find(x => x.name === name);
      if (!sub) throw new Error(`${where}: no saved macro ${name}`);
      if (stack.length >= MAX_DEPTH) throw new Error(`${where}: macros nested more than ${MAX_DEPTH} deep`);
      const run = stepsFor(sub, where), inner: Env = { ...sub.vars, ...run.env };
      for (const [a, v] of Object.entries(s0.args ?? {})) {
        const n = varOf(v), e = n ? null : exprOf(v);
        if (n && !Object.hasOwn(env, n)) throw new Error(`${where}: no value for <${n}> (passed as ${a})`);
        inner[a] = n ? env[n] : e ? mathValue(e, env, `${where} (passed as ${a})`) : v;
      }
      go(run.steps, here, [...stack, name], inner, nd);
    });
  };
  const top = stepsFor(m, m.name.trim() || 'this macro');
  go(top.steps, [], m.name.trim() ? [m.name.trim()] : [], { ...m.vars, ...top.env, ...env }, top.node);
  return out;
}

/**
 * The variables m's steps use, in the order they first come, each with the inputs it goes to: a step's own, and
 * through a Macro step's args (<name> passed on) the inputs that macro's variable goes to. A For each's variable in
 * the steps inside it is its own, not the macro's. A variable used in math (<w*h>) goes to an input taking any number.
 */
/** The input a variable used in math (<w*h>) goes to: any number. */
const anyNum = (key: string): Field => ({ k: 'num', key, label: '', dflt: 0 });

export function varsOf(m: Macro, macros: Macro[]): Map<string, Field[]> {
  const memo = new Map<string, Map<string, Field[]>>();
  const of = (m: Macro, stack: string[]): Map<string, Field[]> => {
    const hit = memo.get(m.name);
    if (hit && stack.length) return hit;
    const out = new Map<string, Field[]>(), use = (n: string, fs: Field[]) => { const x = out.get(n); if (x) x.push(...fs); else out.set(n, [...fs]); };
    const walk = (steps: Step[], own: Set<string>) => {
      for (const s of steps) {
        for (const f of KINDS[s.t].fields as Field[]) {
          const v = s.p[f.key], n = varOf(v);
          if (n) { if (f.k !== 'var' && f.k !== 'slot' && !own.has(n)) use(n, [f]); continue; }
          const e = mathOk(f) ? exprOf(v) : null;
          if (e) for (const x of exprNames(e)) if (!own.has(x)) use(x, [anyNum(x)]);
        }
        if (s.t === 'blobs') walk(s.steps ?? [], new Set([...own, String(s.p.var)]));
        if (s.t !== 'macro' || !s.args) continue;
        const name = String(s.p.name), sub = macros.find(x => x.name === name);
        const inner = sub && !stack.includes(name) && stack.length < MAX_DEPTH ? of(sub, [...stack, name]) : null;
        for (const [a, v] of Object.entries(s.args)) {
          const n = varOf(v), e = n ? null : exprOf(v);
          if (n) { if (!own.has(n)) use(n, inner?.get(a) ?? []); } else if (e) for (const x of exprNames(e)) if (!own.has(x)) use(x, [anyNum(x)]);
        }
      }
    };
    walk(stepsOf(m), new Set());
    if (stack.length) memo.set(m.name, out);
    return out;
  };
  return of(m, m.name.trim() ? [m.name.trim()] : ['']);
}

/** m's data variables (those its steps use only to name a data group): a Macro or For each node's ports for it (nodes.ts). */
export const dataVars = (m: Macro, macros: Macro[]): string[] =>
  [...varsOf(m, macros)].filter(([, fs]) => fs.length && fs.every(f => f.k === 'data')).map(([n]) => n);

/** Field f is in use with the step's parameters p (a field for one data source isn't for the others). */
export const shown = (f: Field, p: Params) => !f.on || f.on[1].includes(String(p[f.on[0]]));

/** "∩ Normal up/down (z) within 20°": a step in a line. */
export function describe(s: Step): string {
  const k: Kind = KINDS[s.t];
  if (s.t === 'tune') {
    const { req, deny } = tuneCounts(s), bools = k.fields.filter(f => f.k === 'bool' && TUNABLE.has(f.key as StepKind));
    const ticked = bools.filter(f => s.p[f.key]).map(f => f.label), data = s.p.data ? [`Data band of ${s.p.data}`] : [];
    const on = ticked.length === bools.length ? ['every rule', ...data] : [...ticked, ...data];
    const passes = +s.p.passes > 1 ? ` × ${s.p.passes} passes` : '', morph = s.p.morph ? `, erode/dilate by up to ${s.p.mr} m` : '';
    return `${MODES.find(([m]) => m === s.mode)?.[1] ?? ''} ${k.label} ${s.p.iters} rounds${passes} (${on.join(', ') || 'no rules'}${morph}) · require ${req.toLocaleString()} / deny ${deny.toLocaleString()}`;
  }
  const mode = k.combines ? `${MODES.find(([m]) => m === s.mode)?.[1] ?? ''} ` : '', inv = s.p.inv === true ? 'not ' : typeof s.p.inv === 'string' ? `${s.p.inv} ` : '';
  const ps = k.fields.filter(f => f !== INV && shown(f, s.p)).map(f => {
    const v = s.p[f.key];
    if (f.k === 'macro' && v === '') return '(none picked)';
    if (f.k === 'num' && f.blank !== undefined && v === 0) return '';   // left empty: the default it stands for
    if ((f.k === 'group' || f.k === 'data') && v === '') return '';
    if (f.k === 'src' && v === 'sel') return `${f.label ? f.label + ' ' : ''}the selection`;
    const shown = f.k === 'choice' ? f.options.find(([o]) => o === v)?.[1] ?? v : f.k === 'bool' ? (v ? f.label : '') : f.k === 'var' ? `<${v}>` : String(v);
    return f.k === 'bool' ? shown : `${f.label ? f.label + ' ' : ''}${shown}${f.k === 'num' && f.unit ? (f.unit === '°' ? '°' : ' ' + f.unit) : ''}`;
  }).filter(Boolean);
  const args = s.t === 'macro' && s.args ? Object.entries(s.args).map(([n, v]) => `${n}=${v}`) : [];
  const body = s.t === 'blobs' ? `: ${s.steps?.length ?? s.body ?? 0} step${(s.steps?.length ?? s.body) === 1 ? '' : 's'}` : '';
  return `${mode}${inv}${k.label}${ps.length ? ' ' + ps.join(' ') : ''}${args.length ? ` with ${args.join(', ')}` : ''}${body}`;
}
